/**
 * Agent Table Column Definitions
 * 
 * TanStack Table column definitions for Agents table
 * Includes custom cell renderers for status badges, dates, and actions
 */

import { ColumnDef } from '@tanstack/react-table';
import { FiEdit2, FiEye, FiMonitor, FiTrash2 } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { StatusBadge } from '@/components/Common/StatusBadge';
import type { AgentListItem, AgentStatus } from '@/types';
import styles from './agentColumns.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  
  if (diffInMinutes < 1) return 'Agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;
  
  return formatDate(dateString);
};

/**
 * Agent Actions Props
 */
export interface AgentActionsProps {
  onEdit?: (agent: AgentListItem) => void;
  onView?: (agent: AgentListItem) => void;
  onDelete?: (agent: AgentListItem) => void;
}

/**
 * Create agent columns for TanStack Table
 */
export const createAgentColumns = (actions: AgentActionsProps): ColumnDef<AgentListItem>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Nome do Agente',
    cell: ({ row }) => (
      <div 
        className={styles.agentInfo}
        onClick={() => actions.onView?.(row.original)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.agentIcon}>
          <FiMonitor size={18} />
        </div>
        <div className={styles.agentDetails}>
          <span className={styles.agentName}>{row.original.name}</span>
        </div>
      </div>
    ),
    enableSorting: true,
  },
  {
    id: 'hostname',
    accessorKey: 'hostname',
    header: 'Hostname',
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return (
        <span className={styles.hostnameCell}>
          {value || '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: 'os',
    accessorKey: 'os',
    header: 'Sistema',
    cell: ({ row }) => {
      const os = row.original.os;
      
      return (
        <div className={styles.osCell}>
          {os ? (
            <span className={styles.osName}>{os}</span>
          ) : (
            '-'
          )}
        </div>
      );
    },
    enableSorting: true,
  },
  {
    id: 'agentVersion',
    accessorKey: 'agentVersion',
    header: 'Versão',
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return (
        <span className={styles.versionBadge}>
          {value || '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as AgentStatus | undefined;
      const displayStatus = status || 'offline';
      
      // Map agent status to a general status for badge display
      const statusMap: Record<string, string> = {
        'online': 'active',
        'offline': 'inactive',
        'inactive': 'inactive',
        'error': 'blocked',
      };
      
      return (
        <StatusBadge 
          status={statusMap[displayStatus] as any} 
          type="user"
        />
      );
    },
    enableSorting: true,
  },
  {
    id: 'lastSeenAt',
    accessorKey: 'lastSeenAt',
    header: 'Última Conexão',
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return (
        <span className={styles.dateCell} title={value ? formatDate(value) : undefined}>
          {value ? formatRelativeTime(value) : 'Nunca'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Criado em',
    cell: ({ getValue }) => (
      <span className={styles.dateCell}>{formatDate(getValue() as string)}</span>
    ),
    enableSorting: true,
  },
  {
    id: 'actions',
    header: 'Ações',
    cell: ({ row }) => {
      const agent = row.original;
      
      return (
        <div className={styles.actionsWrapper}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onView?.(agent)}
            title="Visualizar detalhes"
            iconLeft={<FiEye size={16} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit?.(agent)}
            title="Editar agente"
            iconLeft={<FiEdit2 size={16} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onDelete?.(agent)}
            title="Deletar agente"
            iconLeft={<FiTrash2 size={16} />}
            className={styles.deleteButton}
          />
        </div>
      );
    },
    enableSorting: false,
  },
];
